"use client";
import React, { useState, useEffect, useRef } from "react";
import { Container } from "react-bootstrap";
import { useConfig } from "@/lib/providers/ConfigProvider";
import ProfileCoverPhoto from "@/components/shared/ProfileCoverPhoto";
import EventDateAndTime from "@/components/shared/EventDateAndTime";
import MapLink from "@/components/shared/MapLink";
import EventParticipantGroupParticipantList from "@/components/events/EventParticipantGroupParticipantList";
import EventParticipantGroupVendorList from "@/components/events/EventParticipantGroupVendorList";
import EventKioskScheduleView from "@/components/events/EventKioskScheduleView";
import MapRouteDetailView from "@/components/maps/MapRouteDetailView";
import EventKioskMapView from "@/components/events/EventKioskMapView";
import "@/components/events/EventKioskView.css";

var moment = require("moment");

const EventKioskView = ({
  name,
  profile,
  event,
  eventMap,
  managedEventParticipantGroups,
  allParticipants,
  participantPoints,
  points,
  amenityPoints,
  tabOptions,
  eventSchedule,
  mapRoutes,
  flattenedParticipants
}) => {
  const { webUrl,apiUrl } = useConfig();
  const [kioskPage, setKioskPage] = useState("splash");
  const [fading, setFading] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [tiles, setTiles] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const contentRef = useRef(null);

  var startDate = moment(event.startDate.date);
  if (event.recurringOptions && event.recurringDates) {
    var now = moment();
    var isoDate = event.recurringDates.find(d => moment(d).isAfter(now));
    if (isoDate) {
      startDate = moment(isoDate);
    }
  }
  const endDate = moment(event.endDate.date);

  const navigate = page => {
    setFading(true);
    setTimeout(() => {
      console.log("navigating to", page);
      setKioskPage(page);
      setFading(false);
      if (contentRef.current) {
        contentRef.current.scrollTo({ top: 0, behavior: "instant" });
      }
    }, 180);
  };

  useEffect(() => {
    if (tabOptions && managedEventParticipantGroups) {
      const tiles = buildTiles();
      console.log("tiles", tiles);
      setTiles(tiles);
      setLoaded(true);
    }
  }, [tabOptions, managedEventParticipantGroups]);

  const buildTiles = () => {
    // Participant group tiles — driven by managedEventParticipantGroups directly
    const groupTiles = [];
    if (managedEventParticipantGroups) {
      Object.keys(managedEventParticipantGroups).forEach(key => {
        const epg = managedEventParticipantGroups[key];
        if (epg && epg.enabled) {
          const iconCfg = epg.iconConfiguration;
          groupTiles.push({
            page: key.toLowerCase(),
            iconType: "fa",
            icon: iconCfg && iconCfg.icon ? iconCfg.icon : "people-group",
            iconBgColor: iconCfg && iconCfg.backgroundColor
              ? `#${iconCfg.backgroundColor}`
              : null,
            label: epg.label || key.charAt(0).toUpperCase() + key.slice(1).toLowerCase()
          });
        }
      });
    }

    // Built-in tiles — map, gallery, schedule, routes — from tabOptions, skipping group entries
    const builtinTiles = tabOptions
      .filter(to =>
        to.value === "home" ||
        to.value === "gallery" ||
        to.value === "schedule"
      )
      .map(to => {
        if (to.value === "home") {
          return { page: "map", iconType: "fa", icon: "map", label: "Interactive Map" };
        }
        if (to.value === "gallery") {
          return { page: "gallery", iconType: "fa", icon: "gallery-thumbnails", label: "Gallery" };
        }
        if (to.value === "schedule") {
          return { page: "schedule", iconType: "fa", icon: "calendar", label: "Schedule" };
        }
      });

    return [...groupTiles, ...builtinTiles];
  };

  const renderSubPageContent = () => {
    if (kioskPage === "map") {
      return (
        <EventKioskMapView
          name={name}
          event={event}
          eventMap={eventMap}
          managedEventParticipantGroups={managedEventParticipantGroups}
          allParticipants={allParticipants}
          participantPoints={participantPoints}
          points={points}
          amenityPoints={amenityPoints}
          mapRoutes={mapRoutes}
        />
      );
    }

    if (kioskPage === "gallery") {
      return (
        <div className="kiosk-subpage-body">
          <p className="muted text-center" style={{ marginTop: "2rem" }}>
            Gallery view coming soon.
          </p>
        </div>
      );
    }

    if (kioskPage === "schedule") {
      return (
        <EventKioskScheduleView
          event={event}
          eventSchedule={eventSchedule}
          flattenedParticipants={flattenedParticipants}
          startDate={startDate}
        />
      );
    }

    if (kioskPage.startsWith("route-")) {
      const idx = parseInt(kioskPage.replace("route-", ""), 10);
      const route = mapRoutes && mapRoutes.filter(r => r.enableTab)[idx];
      if (!route) return null;
      return (
        <div className="kiosk-subpage-body">
          <MapRouteDetailView
            key={`kiosk-map-route-${idx}`}
            route={route}
            map={eventMap}
          />
        </div>
      );
    }

    if (managedEventParticipantGroups) {
      const epg = managedEventParticipantGroups[kioskPage.toUpperCase()];
      if (epg && epg.enabled) {
        const buttonRef = React.createRef();
        let pgPoints = [];
        if (kioskPage !== "market" && participantPoints) {
          const pointsKey = Object.keys(participantPoints).find(k =>
            k.endsWith(kioskPage.toUpperCase())
          );
          if (pointsKey) pgPoints = participantPoints[pointsKey];
        }
        return (
          <div className="kiosk-subpage-body">
            {kioskPage === "market" ? (
              <EventParticipantGroupVendorList
                name={name}
                event={event}
                eventParticipantGroup={epg}
                participants={
                  allParticipants.hasOwnProperty(epg.type)
                    ? allParticipants[epg.type]
                    : []
                }
                buttonRef={buttonRef}
              />
            ) : (
              <EventParticipantGroupParticipantList
                name={name}
                event={event}
                eventParticipantGroup={epg}
                points={pgPoints}
              />
            )}
          </div>
        );
      }
    }

    return null;
  };

  if (!loaded) {
    return null;
  }

  const TileIcon = ({ tile, index }) => {
    return (
      <>
      { tile.icon && tile.iconType === "fa" ? (
        <span key={`kiosk-tile-icon-${index}`} className="kiosk-tile-icon">
          <i className={`fas fa-${tile.icon}`} />
        </span>
      ) : (
        <span key={`kiosk-tile-icon-${index}`} className="kiosk-tile-icon">
          <i className="fas fa-people-group" />
        </span>
      )}
      </>
    );
  }

  const eventUrl = `${webUrl}${name}/events/${event.id}?mode=kiosk`;
  const smsHref = `sms:?body=${encodeURIComponent(`Check out ${event.title}: ${eventUrl}`)}`;

  const ShareButton = () => (
    <button
      className="kiosk-bar-action"
      aria-label="Share"
      onClick={() => setShowShareDialog(true)}
    >
      <i className="fas fa-share" />
    </button>
  );

  const BackButton = ({ onClick }) => (
    <button className="kiosk-bar-action kiosk-back-btn" onClick={onClick}>
      <i className="fas fa-chevron-left" />
      <span>Back</span>
    </button>
  );

  return (
    <div className={`kiosk-page${fading ? " fading" : ""}`} ref={contentRef}>

      {/* ── Share dialog (full-screen overlay) ─────────────────── */}
      {showShareDialog && (
        <div className="kiosk-share-dialog">
          <div className="kiosk-share-dialog-bar">
            <div className="kiosk-bar-left" />
            <span className="kiosk-bar-center">Share this Event</span>
            <div className="kiosk-bar-right">
              <button
                className="kiosk-bar-action"
                aria-label="Close"
                onClick={() => setShowShareDialog(false)}
              >
                <i className="fas fa-times" />
              </button>
            </div>
          </div>
          <div className="kiosk-share-dialog-body">
            <p className="kiosk-share-hint">
              Scan the QR code to open this event on your device, or send a
              link via text message.
            </p>
            <img
                  src={`${apiUrl}/api/profiles/${profile.resourceName}/events/${event.id}/urcode?size=480&mode=kiosk`}
                />
            <a href={smsHref} className="btn btn-blue with-icon">
              <i className="fas fa-comment-sms" />
              <span>Send via Text</span>
            </a>
          </div>
        </div>
      )}

      {kioskPage === "splash" ? (
        /* ── Splash screen ───────────────────────────────────────── */
        <div className="kiosk-splash">
          {event.coverPhotoAsset && (
            <div className="kiosk-cover-photo">
              <ProfileCoverPhoto
                coverPhotoId={event.coverPhotoAsset.id}
                aspectRatio="16x9"
                coverRatio="16x9"
                maxHeight="280px"
              />
            </div>
          )}
          <Container className="kiosk-splash-content">
            <h2 className="kiosk-event-title">{event.title}</h2>
            {event.subtitle && (
              <p className="kiosk-event-subtitle">{event.subtitle}</p>
            )}
            <div className="kiosk-event-meta">
              <EventDateAndTime
                startDate={startDate}
                endDate={endDate}
                multiDay={event.multiDay}
                titleAs="p"
                subtitleAs="p"
                className="kiosk-date"
              />
            </div>
            {event.description && (
              <div className="kiosk-about">
                <h5>About the Event</h5>
                <p>{event.description}</p>
              </div>
            )}
            <div className="kiosk-whats-inside">
              <h5>What&rsquo;s Inside</h5>
              <div className="kiosk-splash-tiles">
                {tiles.map((tile, index) => (
                  <div
                    key={tile.page}
                    className="kiosk-tile kiosk-tile-list"
                    onClick={() => navigate(tile.page)}
                  >
                    <TileIcon tile={tile} index={index} />
                    <span className="kiosk-tile-label">{tile.label}</span>
                    <span className="material-icons kiosk-tile-chevron">
                      chevron_right
                    </span>
                  </div>
                ))}
              </div>
              <button
                className="btn btn-primary kiosk-explore-btn"
                onClick={() => navigate("home")}
              >
                Explore the Event
              </button>
            </div>
          </Container>
        </div>
      ) : kioskPage === "home" ? (
        /* ── Home: 2-column tile grid ────────────────────────────── */
        <div className="kiosk-home">
          <div className="kiosk-back-bar">
            <div className="kiosk-bar-left">
            </div>
            <span className="kiosk-bar-center">{event.title}</span>
            <div className="kiosk-bar-right">
              <ShareButton />
            </div>
          </div>
          <Container className="kiosk-home-grid-container">
            <div className="kiosk-home-grid">
              {tiles.map(tile => (
                <div
                  key={tile.page}
                  className="kiosk-tile kiosk-tile-card"
                  onClick={() => navigate(tile.page)}
                >
                  <TileIcon tile={tile} />
                  <span className="kiosk-tile-label">{tile.label}</span>
                </div>
              ))}
            </div>
          </Container>
        </div>
      ) : (
        /* ── Content sub-pages ───────────────────────────────────── */
        <div className="kiosk-subpage">
          <div className="kiosk-back-bar">
            <div className="kiosk-bar-left">
              <BackButton onClick={() => navigate("home")} />
            </div>
            <span className="kiosk-bar-center">
              {tiles.find(t => t.page === kioskPage)?.label ?? ""}
            </span>
            <div className="kiosk-bar-right">
              <ShareButton />
            </div>
          </div>
          {renderSubPageContent()}
        </div>
      )}
    </div>
  );
};

export default EventKioskView;
